import { type PermissionProjectPermissions } from "../../SettingsPermissions/types/permissions.types.ts"
import type { Project } from "../types/project.types.ts"

// EAD-642 files a project with a flag and leaves its status alone, so the
// archived test is the flag — a status check would miss it entirely.
export const isArchivedProject = (
  project: Project | null | undefined
): boolean => !!project?.is_archived

export const canEditProject = (
  permissions: PermissionProjectPermissions | undefined,
  project: Project | null | undefined
): boolean =>
  !!permissions?.projects?.project_edit &&
  !!permissions?.projects?.project_view &&
  !!project &&
  !project.is_archived

// Every write right in the project tree ends one of these; the read rights and
// the archive right itself do not, which is what leaves Restore offered.
const WRITE_SUFFIXES = ["_create", "_edit", "_delete", "_full_access"]

// Two writes that are named rather than suffixed. `project_approval` is not one
// of them: it gates the lead-approval trigger on the Projects list, which reads
// the permission tree directly rather than through this project's selector.
const WRITE_FEATURES = ["project_budget_approval", "project_assign_pm"]

const isWrite = (code: string) =>
  WRITE_SUFFIXES.some((suffix) => code.endsWith(suffix)) ||
  WRITE_FEATURES.includes(code)

const withoutWrites = (codes: Record<string, boolean | undefined>) =>
  Object.fromEntries(
    Object.entries(codes).map(([code, granted]) => [
      code,
      isWrite(code) ? false : granted
    ])
  )

/**
 * The caller's project permissions, with every write cleared while the project
 * is archived.
 *
 * Each section derives its add / edit / delete affordances from this tree, so
 * clearing it once is what makes the whole page read-only.
 */
export const projectWritePermissions = <
  T extends PermissionProjectPermissions | undefined
>(
  permissions: T,
  project: Project | null | undefined
): T => {
  if (!permissions || !project?.is_archived) return permissions

  return Object.fromEntries(
    Object.entries(permissions).map(([group, codes]) => [
      group,
      codes ? withoutWrites(codes) : codes
    ])
  ) as T
}
